/* eslint-disable @typescript-eslint/no-unused-vars */
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import {
  Severity,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import { MIN_CLEARANCE, SEAL_INCLINATION_LIMIT } from "../validatorConstants";

export const sealChecksNames = [
  "operation_expansion_volume",
  "pull_expansion_volume",
  "upper_chamber_pull_expansion_volume",
  "operating_seal_thrust_bearing_load",
  "shut_in_seal_thrust_bearing_load",
  "seal_size_top_casing",
  "seal_size_bypass_tubing_top_casing",
  "seal_size_bypass_tubing_bottom_casing",
  "seal_size_bottom_casing",
  "seal_shaft_limit_hs",
  "seal_shaft_limit_std",
  "seal_inclination_warning",
  "seal_inclination_error",
  "espshore_seal_with_hps_selected",
  "hps_seal_with_espshore_selected"
] as const;

export const sealChecks: ValidationRuleGenerator = ({
  calcs,
  parameters,
  wellProperties,
  equipment,
  stack
}) => {
  const { shutInThrustLoadPounds, operatingThrustLoad } =
    calcs.pumpCalculations.aggregate;
  const primarySeal = calcs.primarySeal;
  const primaryPump = calcs.primaryPump;
  const lowerTandemSealCalcs =
    calcs.descendingOrderSeals.value.length > 1
      ? calcs.sealCalculations.individual.value[calcs.bottomSeal?.value?.id]
      : calcs.sealCalculations.individual.value[primarySeal?.value?.id];
  return [
    // Bag and volume checks for system
    {
      name: "operation_expansion_volume",
      message: addStackPrefixToMessage(
        `Expansion Volume Exceeded at Shutdown`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        primarySeal?.value?.id,
        equipment.value?.r_seal_motor_oil?.id,
        equipment.value?.r_motor?.id,
        lowerTandemSealCalcs?.operationExpansionRatio
      ]),
      isInvalid: lowerTandemSealCalcs?.operationExpansionRatio > 1,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "pull_expansion_volume",
      message: addStackPrefixToMessage(
        `Seal Volume Exceeded During Pull`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        primarySeal?.value?.id,
        equipment.value?.r_seal_motor_oil?.id,
        equipment.value?.r_motor?.id,
        lowerTandemSealCalcs?.pullExpansionRatio
      ]),
      isInvalid: lowerTandemSealCalcs?.pullExpansionRatio > 1,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "upper_chamber_pull_expansion_volume",
      message: addStackPrefixToMessage(
        `Upper Seal Chamber contraction volume exceeded during pull`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        primarySeal?.value?.id,
        equipment.value?.r_seal_motor_oil?.id,
        equipment.value?.r_motor?.id
      ]),
      isInvalid:
        calcs.sealCalculations.aggregate.sealUpperChamberPullExpansionRatio
          .value >= 1,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "operating_seal_thrust_bearing_load",
      message: addStackPrefixToMessage(
        `Seal Operational Thrust exceeds Bearing Rating`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        primarySeal?.value?.id,
        primaryPump?.value?.id,
        equipment.value?.r_seal_motor_oil?.id,
        equipment.value?.r_motor?.id
      ]),
      isInvalid:
        operatingThrustLoad.value >
        calcs.sealCalculations.individual.value[primarySeal?.value?.id]
          ?.bearingRatingAtRPMLBS,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "shut_in_seal_thrust_bearing_load",
      message: addStackPrefixToMessage(
        `Seal Shut-In Thrust exceeds Bearing Rating`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([
        primarySeal?.value?.id,
        equipment.value?.r_seal_motor_oil?.id,
        equipment.value?.r_motor?.id,
        primaryPump?.value?.id
      ]),
      isInvalid:
        shutInThrustLoadPounds.value >
        calcs.sealCalculations.individual.value[primarySeal?.value?.id]
          ?.bearingRatingAtRPMLBS,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_size_top_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Seal too large for top casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_top_casing?.drift_inches,
          wellProperties.value?.top_casing_length_feet
        ]) && calcs.descendingOrderSeals.value.length > 0,
      isInvalid: calcs.descendingOrderSeals.value.some(xref => {
        return (
          wellProperties.value?.r_top_casing?.drift_inches -
            calcs.sealCalculations.individual.value[xref.id]
              .maxCalculatedOuterDiameterIn <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.descendingOrderSeals.value
        .filter(xref => {
          return (
            wellProperties.value?.r_top_casing?.drift_inches -
              calcs.sealCalculations.individual.value[xref.id]
                .maxCalculatedOuterDiameterIn <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_size_bypass_tubing_top_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Seal too large with bypass tubing for top casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_top_casing?.drift_inches,
          wellProperties.value?.top_casing_length_feet
        ]) &&
        calcs.descendingOrderSeals.value.length > 0 &&
        !!equipment.value?.use_uphole_y_tool &&
        calcs.orderedYToolBypassTubings.value.length > 0,
      isInvalid: calcs.descendingOrderSeals.value.some(xref => {
        return (
          wellProperties.value?.r_top_casing?.drift_inches -
            (calcs.sealCalculations.individual.value[xref.id].xrefSeal.r_seal
              ?.outer_diameter_inches +
              calcs.yToolBypassTubingMaxODIn.value) <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.descendingOrderSeals.value
        .filter(xref => {
          return (
            wellProperties.value?.r_top_casing?.drift_inches -
              (calcs.sealCalculations.individual.value[xref.id].xrefSeal.r_seal
                ?.outer_diameter_inches +
                calcs.yToolBypassTubingMaxODIn.value) <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_size_bottom_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Seal too large for bottom casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_bottom_casing?.drift_inches,
          wellProperties.value?.bottom_casing_length_feet
        ]) &&
        calcs.equipmentLengths.bottomOfSealsDepthFt.value >
          wellProperties.value?.top_casing_length_feet &&
        calcs.descendingOrderSeals.value.length > 0,
      isInvalid: calcs.descendingOrderSeals.value.some(xref => {
        return (
          wellProperties.value?.r_bottom_casing?.drift_inches -
            calcs.sealCalculations.individual.value[xref.id]
              .maxCalculatedOuterDiameterIn <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.descendingOrderSeals.value
        .filter(xref => {
          return (
            wellProperties.value?.r_bottom_casing?.drift_inches -
              calcs.sealCalculations.individual.value[xref.id]
                .maxCalculatedOuterDiameterIn <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_size_bypass_tubing_bottom_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Seal too large with bypass tubing for bottom casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_bottom_casing?.drift_inches,
          wellProperties.value?.bottom_casing_length_feet
        ]) &&
        calcs.equipmentLengths.bottomOfSealsDepthFt.value >
          wellProperties.value?.top_casing_length_feet &&
        calcs.descendingOrderSeals.value.length > 0 &&
        !!equipment.value?.use_uphole_y_tool &&
        calcs.orderedYToolBypassTubings.value.length > 0,
      isInvalid: calcs.descendingOrderSeals.value.some(xref => {
        return (
          wellProperties.value?.r_bottom_casing?.drift_inches -
            (calcs.sealCalculations.individual.value[xref.id].xrefSeal.r_seal
              ?.outer_diameter_inches +
              calcs.yToolBypassTubingMaxODIn.value) <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.descendingOrderSeals.value
        .filter(xref => {
          return (
            wellProperties.value?.r_bottom_casing?.drift_inches -
              (calcs.sealCalculations.individual.value[xref.id].xrefSeal.r_seal
                ?.outer_diameter_inches +
                calcs.yToolBypassTubingMaxODIn.value) <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_shaft_limit_hs",
      message: addStackPrefixToMessage(
        `HP greater than Seal Shaft Limit (HS)`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([calcs.motorCalculations.totalRequiredBrakeHP]),
      isInvalid: calcs.descendingOrderSeals.value.some(
        xref =>
          calcs.sealCalculations.individual.value[xref.id]
            .shaftPowerLimitHighStrengthHP <
          calcs.motorCalculations.totalRequiredBrakeHP.value
      ),
      affectedXrefIds: calcs.descendingOrderSeals.value
        .filter(
          xref =>
            calcs.sealCalculations.individual.value[xref.id]
              .shaftPowerLimitHighStrengthHP <
            calcs.motorCalculations.totalRequiredBrakeHP.value
        )
        .map(xref => xref.id),
      disablesRules: ["seal_shaft_limit_std"],
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_shaft_limit_std",
      message: addStackPrefixToMessage(
        `HP greater than Seal Shaft Limit (STD), use high-strength shaft`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.descendingOrderSeals.value.some(
        xref =>
          !xref.use_high_strength_shaft &&
          calcs.sealCalculations.individual.value[xref.id]
            .shaftPowerLimitStandardHP <
            calcs.motorCalculations.totalRequiredBrakeHP.value
      ),
      affectedXrefIds: calcs.descendingOrderSeals.value
        .filter(
          xref =>
            !xref.use_high_strength_shaft &&
            calcs.sealCalculations.individual.value[xref.id]
              .shaftPowerLimitStandardHP <
              calcs.motorCalculations.totalRequiredBrakeHP.value
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_inclination_warning",
      message: addStackPrefixToMessage(
        `Bag Seals are recommended due to set depth inclination`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive:
        Boolean(calcs.entryRowsBotToBoe.value) &&
        valuesExist([primarySeal?.value?.id]),
      isInvalid:
        calcs.entryRowsBotToBoe.value?.some(
          ([_md, inclination]: number[]) => inclination > SEAL_INCLINATION_LIMIT
        ) &&
        calcs.descendingOrderSeals.value?.every(
          s => s.r_seal?.bag_chambers === 0
        ),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "seal_inclination_error",
      message: addStackPrefixToMessage(
        `Bag Seals are recommended due to set depth inclination`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        Boolean(calcs.entryRowsAtSeals.value) &&
        valuesExist([primarySeal?.value?.id]),
      isInvalid:
        calcs.entryRowsAtSeals.value?.some(
          ([_md, inclination]: number[]) => inclination > SEAL_INCLINATION_LIMIT
        ) &&
        calcs.descendingOrderSeals.value?.every(
          s => s.r_seal?.bag_chambers === 0
        ),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      disablesRules: ["seal_inclination_warning"]
    },
    {
      name: "hps_seal_with_espshore_selected",
      message: addStackPrefixToMessage(
        "Not Recommended: HPS Seal with ESP Shore",
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([primarySeal?.value?.id]),
      isInvalid:
        calcs.isScenarioEspShore.value &&
        Boolean(primarySeal?.value?.r_seal?.hps_thrust_chamber),
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    },
    {
      name: "espshore_seal_with_hps_selected",
      message: addStackPrefixToMessage(
        "Not Recommended: HPS Pump type with ESP Shore Seal",
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: valuesExist([primarySeal?.value?.id]),
      isInvalid:
        !calcs.isScenarioEspShore.value &&
        !primarySeal?.value?.r_seal?.hps_thrust_chamber,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL
    }
  ];
};
